import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import Note from '@/components/Forms/Note';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAccountNumbersOptions from '@/hooks/useAccountNumbersOptions';
import useAlerts from '@/hooks/useAlerts';
import useDepartmentsOptions from '@/hooks/useDepartmentsOptions';
import GpmCheckboxFiekd from '@/pages/AddInvoice/components/GpmCheckboxFiekd';
import { FreelancerInvoiceSubmissionResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  department_id: number | null;
  finance_account_number_id: number | null;
  gpm: boolean;
  approvement_comment: string;
};

interface Props {
  submission: FreelancerInvoiceSubmissionResource;
  queryKey: string;
  onClose: () => void;
}

const InvoiceApprovalModal: FC<Props> = ({ submission, queryKey, onClose }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const { data: departmentOptions = [] } = useDepartmentsOptions();
  const { data: accountNumberOptions = [] } = useAccountNumbersOptions();

  const filteredDepartmentOptions = departmentOptions.filter(
    (d) => !d.hide_from_dropdowns,
  );

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      department_id: submission.departmentId ?? null,
      finance_account_number_id: submission.financeAccountNumberId ?? null,
      gpm: submission.gpm ?? false,
      approvement_comment: submission.approvementComment ?? '',
    },
  });

  const isGpm = useWatch({ control, name: 'gpm' });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await api.post(
        `/v1/payments/freelancer-invoice-submissions/${submission.id}/approve`,
        data,
      );
      await queryClient.invalidateQueries([queryKey], { exact: false });
      alerts.success('Submission approved successfully');
      onClose();
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal>
      <SidePanel heading="Approve Submission" onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Note>
                  Please check if all information is correct before approving
                  the submission. After approval, the invoice will be processed
                  for payment based on the provided details.
                </Note>
              </Larger>
              <FormField<FormDataT, number>
                control={control}
                name="department_id"
                label="Department"
                type="select"
                options={filteredDepartmentOptions}
              />
              <FormField<FormDataT, number>
                control={control}
                name="finance_account_number_id"
                label="Finance Account Number"
                type="select"
                options={accountNumberOptions}
              />
              {submission.hours != null && (
                <GpmCheckboxFiekd isGpm={isGpm} setValue={setValue} />
              )}
              <FormField<FormDataT>
                control={control}
                name="approvement_comment"
                label="Comment (optional)"
                type="textarea"
                placeholder="Add comment"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Approve
            </Button>
            <Button color="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default InvoiceApprovalModal;
